import dayjs from 'dayjs'

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

/**
 * Serialize a [start, end] date range for a comma-separated URL param.
 *
 * Format: `YYYY-MM-DD,YYYY-MM-DD`. Replaces the previous `Date.toString()`
 * output (e.g. `Sun May 11 2026 00:00:00 GMT-0400 (Eastern Daylight Time)`)
 * which Cloudflare's WAF flagged as suspicious due to the encoded parentheses
 * and spaces.
 *
 * Returns an empty string when either bound is missing so callers can delete
 * the URL param instead of writing `,` into it.
 */
export const serializeDateRangeForUrl = (
  start: Date | null | undefined,
  end: Date | null | undefined
): string => {
  if (!start || !end) return ''
  return `${dayjs(start).format('YYYY-MM-DD')},${dayjs(end).format('YYYY-MM-DD')}`
}

/**
 * Parse a comma-separated date-range URL value into `[startDate, endDate]`.
 *
 * Accepts the new `YYYY-MM-DD,YYYY-MM-DD` shape and falls back to anything
 * `new Date()` can parse, so legacy bookmarks containing the old
 * `Date.toString()` output keep working.
 *
 * For the date-only shape, startDate becomes local start-of-day and endDate
 * becomes local end-of-day — preserving the inclusive range semantics used
 * by the analytics filters. Returns `null` for empty, malformed, or unparsable
 * input so callers can apply their own defaults.
 */
export const parseDateRangeFromUrl = (
  raw: string | null | undefined
): [Date, Date] | null => {
  if (!raw) return null
  const parts = raw.split(',')
  if (parts.length !== 2) return null

  const [startStr, endStr] = parts
  const start = DATE_ONLY_REGEX.test(startStr)
    ? dayjs(startStr).startOf('day').toDate()
    : new Date(startStr)
  const end = DATE_ONLY_REGEX.test(endStr)
    ? dayjs(endStr).endOf('day').toDate()
    : new Date(endStr)

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null
  return [start, end]
}

/**
 * Given a current period [startDate, endDate], returns the prior period with
 * the same inclusive calendar-day count, ending one millisecond before
 * startDate. Mirrors the backend buildPriorPeriod logic.
 */
export const buildPriorPeriod = (
  startDate: Date,
  endDate: Date
): { priorStartDate: Date; priorEndDate: Date } => {
  const rangeMs = endDate.getTime() - startDate.getTime()
  const priorEndDate = new Date(startDate.getTime() - 1)
  return {
    priorEndDate,
    priorStartDate: new Date(priorEndDate.getTime() - rangeMs),
  }
}
